<template>
  <div class="quote-details">
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading && quote"
      class="quote-details-content"
    >
      <div class="quote-details-header">
        <div class="title-text">
          Your Quote
        </div>
        <div class="actions">
          <LockingButton
            v-if="!quote.approvedDate"
            :locked="saving"
            :text="'Approve'"
            @click="onApproveQuoteClicked"
          />
        </div>
      </div>
      <div class="left-column">
        <div class="card">
          <div class="header">
            General Information
          </div>
          <div class="content">
            <TextInput
              v-model="quote.buildTypeName"
              label="Build Type"
              class="input"
              :disabled="true"
            />
            <TextInput
              v-if="quote.cartridgeName != ''"
              v-model="quote.cartridgeName"
              label="Cartridge"
              class="input"
              :disabled="true"
            />
            <NumericInput
              v-if="quote.partsDiscountPercentage > 0"
              v-model="quote.partsDiscountPercentage"
              label="Parts Discount %"
              class="input"
              :disabled="true"
            />
            <NumericInput
              v-if="quote.servicesDiscountPercentage > 0"
              v-model="quote.servicesDiscountPercentage"
              label="Services Discount %"
              class="input"
              :disabled="true"
            />
            <div class="input-label">
              Total
            </div>
            <div class="total">
              {{ $methods.formatPrice(quote.total) }}
            </div>
            <div
              v-if="quote.approvedDate"
              class="approval-info"
            >
              Approved {{ $methods.formatDate(quote.approvedDate) }}
            </div>
            <div
              v-if="quote.expirationDate"
              class="approval-info"
            >
              Expiration Date {{ $methods.formatDate(quote.expirationDate) }}
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Your Information
          </div>
          <div class="content">
            <div class="customer-tile">
              <div class="customer-tile-content">
                <div
                  v-if="quote.customer.firstName || quote.customer.lastName"
                  class="name"
                >
                  {{ quote.customer.firstName }} {{ quote.customer.lastName }}
                </div>
                <div
                  v-if="
                    quote.customer.addressLine1 ||
                      quote.customer.addressLine2 ||
                      quote.customer.city ||
                      stateDisplayValue ||
                      quote.customer.postalCode
                  "
                  class="address"
                >
                  <div v-if="quote.customer.addressLine1">
                    {{ quote.customer.addressLine1 }}
                  </div>
                  <div v-if="quote.customer.addressLine2">
                    {{ quote.customer.addressLine2 }}
                  </div>
                  <div
                    v-if="
                      quote.customer.city ||
                        stateDisplayValue ||
                        quote.customer.postalCode
                    "
                  >
                    <span
                      v-if="quote.customer.city"
                    >{{ quote.customer.city }}, </span><span v-if="stateDisplayValue">{{
                      stateDisplayValue + " "
                    }}</span><span v-if="quote.customer.postalCode">{{
                      quote.customer.postalCode
                    }}</span>
                  </div>
                </div>
                <a v-if="quote.customer.phoneNumber">
                  {{ $methods.formatPhoneNumber(quote.customer.phoneNumber) }}
                </a>
                <a v-if="quote.customer.email">
                  {{ quote.customer.email }}
                </a>
              </div>
              <DotsSvg 
                :tabindex="0"
                @keyup.enter="showMenu = true"
                @click.stop="showMenu = true" 
              />
              <div
                v-if="showMenu"
                class="menu"
              >
                <div v-click-outside="closeMenu">
                  <div
                    class="menu-option"
                    :tabindex="0"
                    @keyup.enter="onEditAccountInformationClicked"
                    @click="onEditAccountInformationClicked"
                    @blur="showMenu = false"
                  >
                    Edit Your Information
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="right-column">
        <div class="card">
          <div class="header">
            Your Supplied Parts
          </div>
          <div class="content customer-supplied-parts">
            {{ quote.customerSuppliedParts }}
          </div>
        </div>
        <div class="card">
          <div class="header">
            Parts
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort">
                      <div>
                        Item
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Inventory Status
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody v-if="quote.parts.length > 0">
                  <tr
                    v-for="quotePart in quote.parts"
                    :key="quotePart.name"
                  >
                    <td>{{ quotePart.name }}</td>
                    <td>{{ $methods.formatPrice(quotePart.price) }}</td>
                    <td>{{ quotePartStatusText(quotePart.status) }}</td>
                    <td class="notes">
                      <CustomerNoteTile
                        v-for="note in quotePart.notes"
                        :key="note.content"
                        :note="note"
                      />
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="4"
                      class="message"
                    >
                      There are no parts on your quote.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="header">
            Services
          </div>
          <div class="content">
            <div class="overflow-wrapper">
              <table>
                <thead>
                  <tr>
                    <th class="no-sort">
                      <div>
                        Name
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Description
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Price
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Fields
                      </div>
                    </th>
                    <th class="no-sort">
                      <div>
                        Notes
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody v-if="quote.services.length > 0">
                  <tr
                    v-for="quoteService in quote.services"
                    :key="quoteService.name"
                  >
                    <td>{{ quoteService.name }}</td>
                    <td class="description">
                      {{ quoteService.description }}
                    </td>
                    <td>{{ $methods.formatPrice(quoteService.price) }}</td>
                    <td class="services">
                      <div
                        v-for="(serviceFieldValue, index) in quoteService.serviceFieldValues"
                        :key="index"
                        class="service"
                      >
                        <TextInput
                          v-model="serviceFieldValue.value"
                          :label="serviceFieldValue.name"
                          class="input"
                          :disabled="true"
                        />
                      </div>
                    </td>
                    <td class="notes">
                      <CustomerNoteTile
                        v-for="note in quoteService.notes"
                        :key="note.content"
                        :note="note"
                      />
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="5"
                      class="message"
                    >
                      There are no services on your quote.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <transition name="slide-up">
    <QuoteApprovalModal 
      v-if="showQuoteApprovalModal"
      :quote-id="id"
      :saving="saving"
      @approved="onQuoteApproved"
      @close="showQuoteApprovalModal = false"
    />
  </transition>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import CustomerNoteTile from '@/components/elements/CustomerNoteTile.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { CustomerQuoteService } from '@/services/customerQuote.service'
import { CustomerQuoteDetails } from '@/@types/responses/customer_quote/CustomerQuoteDetails'
import LockingButton from '@/components/shared/LockingButton.vue'
import QuoteApprovalModal from '@/components/modals/QuoteApprovalModal.vue'
import DotsSvg from '@/components/svgs/DotsSvg.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import { StateService } from '@/services/states.service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'MyQuote',
  components: {
    CustomerNoteTile,
    DotsSvg,
    LoadingPulse,
    LockingButton,
    NumericInput,
    QuoteApprovalModal,
    TextInput
  },
  beforeRouteLeave() {
    if (this.showQuoteApprovalModal) {
      this.showQuoteApprovalModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const customerQuoteService = inject(PROVIDER_CONSTANTS.customerQuoteService) as CustomerQuoteService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      customerQuoteService,
      stateService,
      toast
    }
  },
  data() {
    let quote: CustomerQuoteDetails|undefined

    return {
      loading: true,
      quote,
      saving: false,
      showMenu: false,
      showQuoteApprovalModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
    }
  },
  computed: {
    stateDisplayValue(): string|undefined {
      if (this.quote && this.quote.customer.stateId) {
        const stateDropdownOption = this.stateDropdownOptions.find(o => o.id == this.quote?.customer.stateId)
        return stateDropdownOption?.name
      }

      return undefined
    }
  },
  mounted() {
    this.getStates().then(() => {
      this.getQuote()
    })
  },
  methods: {
    closeMenu(): void {
      this.showMenu = false
    },
    getQuote(): Promise<void> {
      return this.customerQuoteService.getCustomerQuote(this.id).then(quote => {
        this.quote = quote
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to load your quote. Try reloading the page.')
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onApproveQuoteClicked(): void {
      this.showQuoteApprovalModal = true
    },
    onEditAccountInformationClicked(): void {
      this.$router.push('/my-account')
    },
    onQuoteApproved(): void {
      if (!this.quote) return

      this.saving = true

      this.customerQuoteService.updateCustomerQuote(this.id).then(() => {
        this.getQuote().then(() => {
          this.toast.success('Your quote was approved successfully.')
          this.saving = false
          this.showQuoteApprovalModal = false
        })
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse
          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return
        }

        this.toast.error('Failed to approve your quote. Please try again.')
      })
    },
    quotePartStatusText(status: number): string {
      switch(status) {
        case 1: return 'In Stock'
        case 2: return 'On Order'
        case 3: return 'Needs Ordered'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.quote)
        document.title = `Quote #${this.quote.id} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.quote-details {
  background-color: $Color-fill-gray;
  position: relative;
  min-height: calc(100vh - 94px);

  @include desktop {
    min-height: calc(100vh - 130px);
  }

  > .loading-spinner {
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translate(50%, 50%);
  }

  > .quote-details-content {
    display: grid;
    column-gap: 20px;
    grid-template-areas: 
      "header"
      "left"
      "right";
      grid-template-columns: 100%;

    @include desktop {
      grid-template-columns: 340px minmax(0, 1fr);
      grid-template-areas:
        "header header"
        "left right";
    }

    > .quote-details-header {
      grid-area: header;
      padding: 15px 15px 10px 15px;
      margin-bottom: 15px;
      border-bottom: 1px solid $Color-border-gray;
      display: flex;
      position: sticky;
      background-color: $Color-fill-gray;
      z-index: 100;
      top: 94px;

      @include desktop {
        flex-direction: row;
        top: 130px;
      }

      > .title-text {
        line-height: 37px;
        font-size: 24px;
        font-weight: bold;
      }

      > .actions {
        margin-left: auto;
      }
    }

    > .left-column {
      grid-area: left;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-right: 0;
      }
    }

    > .right-column {
      grid-area: right;
      padding-left: 15px;
      padding-right: 15px;

      @include desktop {
        padding-left: 0;
      }
      
      > .message {
        font-style: italic;
        color: $Color-font-medium;
      }
    }

    .card {
      background-color: $Color-white;
      margin-bottom: 15px;
      @include element-shadow;

      > .header {
        background-color: $Color-fill-black;
        color: $Color-white;
        padding: 15px;
        position: relative;
      }

      > .content {
        padding: 15px;

        &.customer-supplied-parts {
          white-space: pre;
        }

        > .input {
          margin-bottom: 10px;
        }

        > .input-label {
          @include input-text-field-label;
        }

        > .total {
          margin-bottom: 15px;
          font-weight: 600;
          color: $Color-font-high;
          font-size: 1.3rem;
        }

        > .approval-info {
          margin-top: 15px;
          font-size: 12px;
          line-height: 18px;
          font-weight: normal;
          color: $Color-font-medium;
        }

        > .customer-tile {
          position: relative;
          background-color: $Color-fill-gray;
          padding: 15px;
          border-radius: 6px;
          display: flex;

          > .customer-tile-content {
            flex: 0 0 90%;
            display: flex;
            flex-direction: column;
            line-height: 24px;

            > .name {
              color: $Color-font-high;
              font-weight: 600;
            }

            > .address {
              color: $Color-font-medium;
              font-size: 15px;
            }

            > a {
              color: $Color-font-medium;
              text-decoration: none;
              font-style: italic;
              font-size: 15px;
            }
          }

          > svg {
            flex: 0 0 10%;
            margin: auto 10px;
          }

          > .menu {
            @include menu;
          }
        }

        > .overflow-wrapper {
          overflow-y: auto;
          @include sac-table;

          td.description {
            white-space: break-spaces;
          }

          td.services {
            padding-top: 15px;
            min-width: 450px;

            > .service {
              margin-bottom: 15px;

              &:last-of-type {
                margin-bottom: 0;
              }
            }
          }

          td.notes {
            min-width: 300px;
          }
        }
      }
    }
  }
}

.slide-up {
  @include modal-animation;
}
</style>
